import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import useFetch from "../../../../hooks/useFetch";
import AILightComponent from "../../../../components/AILightComponent";

const today = new Date().toISOString().split("T")[0];

const ScorePlusDoctorPatient = () => {
  const { userCode } = useParams();
  const navigate = useNavigate();
  const authState = useSelector((state) => state.authReducer || {});
  const doctorCode = authState?.user?.think5whyscode || authState?.think5whyscode || "";
  const token = authState?.token;
  const [fetchData] = useFetch();

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [enableGenAIHelp, setEnableGenAIHelp] = useState(false);

  const toggleGenAIHelp = () => setEnableGenAIHelp((p) => !p);

  const loadRecords = async () => {
    if (!doctorCode || !userCode) return;

    try {
      setLoading(true);
      setMessage(null);

      const config = {
        url: "/scoreplus/doctor-patient-records",
        method: "post",
        data: {
          viewerCode1: doctorCode,
          userCode,
          fromDate,
          toDate,
        },
        headers: {
          Authorization: token,
        },
      };

      const res = await fetchData(config, { silent: true });

      if (res.success) {
        setRecords(res.records || []);
      } else {
        setMessage(res.message || "Unable to load records");
        setRecords([]);
      }
    } catch (err) {
      console.error(err);
      setMessage("Unable to load records");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, [userCode, doctorCode]);

  const handleSearch = () => {
    loadRecords();
  };

  if (!doctorCode) {
    return (
      <div className="p-6 text-center text-red-400">
        Doctor code not found.
      </div>
    );
  }

  return (
    <div className="p-4 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <button
            onClick={() => navigate("/scoreplus-doctor")}
            className="text-sm text-cyan-400 hover:text-cyan-300 mb-1"
          >
            ← Back to Patient List
          </button>
          <h1 className="text-xl font-bold text-white">
            Patient: <span className="text-cyan-400">{userCode}</span>
          </h1>
        </div>
      </div>

      {/* Date Filters */}
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-4 mb-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs text-zinc-400 block mb-1">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="bg-zinc-800 text-white rounded px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-400 block mb-1">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="bg-zinc-800 text-white rounded px-3 py-1.5 text-sm"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={loading}
            className="bg-cyan-600 hover:bg-cyan-500 disabled:bg-zinc-700 text-white px-4 py-1.5 rounded text-sm font-medium"
          >
            {loading ? "Loading..." : "🔍 Apply"}
          </button>
        </div>
      </div>

      {/* AI Help */}
      <div className="mb-3 flex justify-end">
        <button
          type="button"
          onClick={toggleGenAIHelp}
          className="rounded-lg bg-purple-600 hover:bg-purple-500 w-fit px-3 py-1 text-xs font-semibold text-white"
        >
          {enableGenAIHelp ? "🙈 Hide AI Help" : "🤖 AI Help"}
        </button>
      </div>

      {enableGenAIHelp && (
        <div className="mb-4 w-full rounded-2xl border border-purple-500/20 bg-[#111827] p-2">
          <AILightComponent
            mode="scoreplus_doctor_patient"
            param1={`🩺 Patient ${userCode} – Clinical Activity Insight`}
            param2={`
You are a Clinical Activity & Lifestyle Intelligence Coach assisting a doctor.

Patient Code: ${userCode}
Doctor is reviewing this patient's ScorePlus activity records.

Patient Activity Records:
${records
  .map(
    (r, i) => `
${i + 1}.
• Date: ${new Date(r.date).toLocaleDateString()}
• Focus: ${r.act}
• Activity: ${r.activity}
• Status: ${r.status}
• Duration: ${r.duration} ${r.durationUnit}
• Tags: ${(r.tags || []).join(", ") || "None"}
`
  )
  .join("\n")}

Analyze the overall patterns (not individual records) and provide:

1. 📊 Overall Activity Summary
2. 💪 Positive Patterns / Strengths
3. ⚠️ Areas of Concern or Gaps
4. 🔥 Consistency & Adherence Analysis
5. 🎯 Clinical / Lifestyle Insights relevant for a doctor
6. 📈 Practical Recommendations
7. 🚀 One clear, professional closing note for the doctor

Keep response under 280 words.
Use headings, emojis and bullet points.
Do NOT repeat raw data or JSON.
`}
          />
        </div>
      )}

      {/* Empty State */}
      {!loading && records.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-yellow-500/20 bg-yellow-500/5 px-4 py-10 text-center">
          <div className="mb-3 text-5xl">🔍</div>
          <h3 className="text-sm font-semibold text-yellow-300">No Records Found</h3>
          <p className="mt-2 text-xs text-zinc-400">
            No activities found for this patient in the selected range.
          </p>
        </div>
      )}

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-3">
        {records.map((row) => (
          <div
            key={row._id}
            className="bg-zinc-900 border border-zinc-700 rounded-xl p-4"
          >
            <div className="flex justify-between items-center mb-2">
              <div className="font-semibold text-white">{row.act}</div>
              <span
                className={`text-xs px-2 py-1 rounded-full ${
                  row.status === "Done"
                    ? "bg-green-900 text-green-300"
                    : row.status === "Partial"
                    ? "bg-yellow-900 text-yellow-300"
                    : "bg-red-900 text-red-300"
                }`}
              >
                {row.status}
              </span>
            </div>
            <div className="text-sm text-gray-400">
              📅 {new Date(row.date).toLocaleDateString()}
            </div>
            <div className="text-sm text-gray-300 mt-1">{row.activity}</div>
            <div className="text-sm text-gray-400 mt-1">
              ⏱ {row.duration} {row.durationUnit}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {(row.tags || []).map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-1 rounded-full text-xs bg-cyan-900/40 text-cyan-300 border border-cyan-700"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block bg-zinc-900 border border-zinc-700 rounded-xl overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-800">
            <tr>
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Focus</th>
              <th className="p-2 text-left">Activity</th>
              <th className="p-2 text-left">Duration</th>
              <th className="p-2 text-left">Tags</th>
            </tr>
          </thead>
          <tbody>
            {records.map((row) => (
              <tr key={row._id} className="border-t border-zinc-800">
                <td className="p-2">{new Date(row.date).toLocaleDateString()}</td>
                <td className="p-2">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      row.status === "Done"
                        ? "bg-green-900 text-green-300"
                        : row.status === "Partial"
                        ? "bg-yellow-900 text-yellow-300"
                        : "bg-red-900 text-red-300"
                    }`}
                  >
                    {row.status}
                  </span>
                </td>
                <td className="p-2">{row.act}</td>
                <td className="p-2">{row.activity}</td>
                <td className="p-2">
                  {row.duration} {row.durationUnit}
                </td>
                <td className="p-2">
                  <div className="flex flex-wrap gap-1">
                    {(row.tags || []).map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-full text-xs bg-cyan-900/40 text-cyan-300 border border-cyan-700"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ScorePlusDoctorPatient;